/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  MouseEventHandler,
  SyntheticEvent,
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch} from 'react-redux';
import debounce from 'lodash.debounce';
import * as actions from '@ion/applications/src/pages/admin/groups/state';
import {translate} from '@ion/core/src/locale/locale';
import {error} from '@ion/core/src/logger/logger';
import {loadAllGroupMembers} from '@ion/core/src/services/Connect';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  CRUDPermissions,
  GenericObject,
  Group,
  Link
} from '@ion/core/src/types/entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {isValue} from '@ion/core/src/utils/StringUtil';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {FormTextField} from '@ion/dls/src/components/forms/FormTextField';
import {
  DropDownDefaultLevelProps,
  DropDownInfiniteScrollProps
} from '../components/menus/Dropdown';
import {useAnimatedMount} from '../hooks/useAnimatedMount';
import {useExtendStyles} from '../hooks/useExtendStyles';
import {useFormSave} from '../hooks/useFormSave';
import {FormState, useFormState} from '../hooks/useFormState';
import ConditionalRender from '../render/ConditionalRender';
import Inheritable from './controls/Inheritable';
import {
  getInitialState,
  GroupFormFields,
  serialize,
  SerializedArgs,
  TargetType
} from './AdminGroupEditorUtils';
import AssetDetailView from './AssetDetailView';
import AssetList from './AssetList';
import DeviceDetailView from './DeviceDetailView';
import DeviceList from './DeviceList';
import EditorDialog from './EditorDialog';
import styles from './editorStyles';

export interface AdminGroupLevelProps {
  viewChip?: boolean;
};

export interface AdminGroupEditorProps extends DropDownDefaultLevelProps,
  AdminGroupLevelProps,
  DropDownInfiniteScrollProps {
  open: boolean;
  group?: Group | GenericObject;
  account?: Account | Link;
  clear?: boolean;
  loading?: boolean;
  onSave: (group: Group) => void;
  onCancel?: (event: SyntheticEvent<{}, Event>) => void;
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  handleChooserChange?: Function;
  getActualMembers?: Function;
  viewGroup?: boolean;
  isCtcAdmin?: boolean;
  includeDeviceGridCheckboxes?: boolean;
  includeAssetGridCheckboxes?: boolean;
  onEdit?: MouseEventHandler;
  onViewAsset?: Function;
  onViewDevice?: Function;
  isAdd?: boolean;
  hasInheritableGroups?: boolean;
  groupsPermissions?: CRUDPermissions;
  globalGroup?: Account | Link;
  ancestorsGroups?: GenericObject;
}

const overrides = () => ({
  overrides : {
    marginTop : '16px',
    padding   : '0 16px'
  },
  formActions : {
    display        : 'flex',
    justifyContent : 'flex-end',
    bottom         : '0px'
  }
});

/**
 * The Group editor with set fields and encapsulated logic for handling the form's state,
 * including validation.
 */
export const AdminGroupEditor = ({
  open,
  group,
  account,
  onSave,
  onCancel,
  onCloseDialog,
  getActualMembers,
  onEdit,
  onViewAsset,
  groupsPermissions,
  globalGroup,
  onViewDevice,
  ancestorsGroups,
  isChipClickable = false,
  isCtcAdmin = false,
  hasInheritableGroups = false,
  includeDeviceGridCheckboxes = false,
  includeAssetGridCheckboxes = false,
  clear = false,
  loading = false,
  viewGroup = false,
  viewChip = false,
  isInfiniteScroll = false,
  isAdd = false
}: AdminGroupEditorProps) => {
  const isEditMode                                                = !isObjectEmpty(group);
  const editorStyles                                              = useExtendStyles(styles, overrides);
  const classes                                                   = editorStyles();
  const dispatch                                                  = useDispatch();
  const {isVisible, onRemove, timeout}                            = useAnimatedMount();
  const [formState, setFormState]                                 = useFormState(getInitialState({group}));
  const [deviceDetailedSelectedRow, setDeviceDetailedSelectedRow] = useState([]);
  const [assetDetailedSelectedRow, setAssetDetailedSelectedRow]   = useState([]);
  const [viewDeviceDetailed, setViewDeviceDetailed]               = useState(false);
  const [viewAssetDetailed, setViewAssetDetailed]                 = useState(false);
  const [deviceDetailedDoneClicked, setDeviceDetailedDoneClicked] = useState(false);
  const [assetDetailedDoneClicked, setAssetDetailedDoneClicked]   = useState(false);
  const [groupsLoading, setGroupsLoading]                         = useState(false);
  const [hasAssetSubGroupSwitch, setAssetHasSubGroupSwitch]       = useState(false);
  const [hasDeviceSubGroupSwitch, setDeviceHasSubGroupSwitch]     = useState(false);

  // defines the group translation interpolation options ...
  const translations = {entityLabel: translate('common:entityLabels.group')};

  const serializer = function(state: FormState): GenericObject {
    return serialize({state, account} as unknown as SerializedArgs);
  };

  const onSubmit = useFormSave({
    serialize : serializer,
    formState,
    setFormState,
    onSave
  });

  // when the clear prop is set to true, we clear all form state and subsequent fields ...
  useEffect(() => {
    clear && clearForm();
  }, [clear]);

  const setDetailsFormState = (name: GroupFormFields, selected, isDone) => {
    if (isDone) {
      const items = formState[name].value || [];
      const value  = items ? [...items, ...selected] : selected;
      setFormState({
        [name] : {
          value : !isValue(value, 'none') ? value : null,
          name
        }
      });
    }
  };

  const setSubGroupDetailsFormState = (name: GroupFormFields, value: boolean, isDone: boolean) => {
    if (isDone) {
      setFormState({
        [name] : {
          name,
          value
        }
      });
    }
  };

  const setGroupMemberState = (name: GroupFormFields, selected) => {
    const value  = selected || [];
    setFormState({
      [name] : {
        value : !isValue(value, 'none') ? value : null,
        name
      }
    });
  };

  useEffect(() => {
    /**
    * Retrieve Group Members list using loadAllGroupMembers method
    * and filter only assets and devices type here.
    */
    const membersList = async(group: Group) => {
      const types   = ['asset', 'device'];
      const assets  = [];
      const devices = [];
      try {
        const items = await loadAllGroupMembers({id: group?.id, pageId: 1, pageSize: 100});
        items?.forEach(({link}) => {
          if (types.indexOf(link.rel) > -1) {
            const items = link.rel === 'asset' ? assets : devices;
            items.push({
              id    : entityId(link.href),
              name  : link.title || entityId(link.href),
              _self : link
            });
          }
        });
        getActualMembers({assets: [...assets], devices: [...devices]});
        setGroupMemberState(GroupFormFields.Assets, assets);
        setAssetDetailedSelectedRow(assets);
        setGroupMemberState(GroupFormFields.Devices, devices);
        setDeviceDetailedSelectedRow(devices);
      } catch (err) {
        error(err);
      }
    };
    !isAdd && membersList(group);
  }, []);

  useEffect(() => {
    setDetailsFormState(GroupFormFields.Assets, assetDetailedSelectedRow, assetDetailedDoneClicked);
  }, [assetDetailedDoneClicked]);

  useEffect(() => {
    setDetailsFormState(GroupFormFields.Devices, deviceDetailedSelectedRow, deviceDetailedDoneClicked);
  }, [deviceDetailedDoneClicked]);

  useEffect(() => {
    setSubGroupDetailsFormState(GroupFormFields.AssetSwitch, hasAssetSubGroupSwitch, assetDetailedDoneClicked);
  }, [hasAssetSubGroupSwitch, assetDetailedDoneClicked]);

  useEffect(() => {
    setSubGroupDetailsFormState(GroupFormFields.DeviceSwitch, hasDeviceSubGroupSwitch, deviceDetailedDoneClicked);
  }, [hasDeviceSubGroupSwitch, deviceDetailedDoneClicked]);

  // clears the state of the form and all related fields ...
  function clearForm() {
    setFormState({}, true);
  };

  // debounces textfield changes so as to only update state once the change has been completed ...
  const debounceFieldChange = debounce((target: TargetType) => {
    const {name, value, required = false} = target;
    const error = false;
    setFormState({
      [name] : {
        name,
        value,
        required,
        error        : error,
        errorMessage : ''
      }
    });
  }, 400);

  // invoked when a textfield change is made, delegating the debounce change handler ...
  function onFieldChange(e: ChangeEvent<HTMLInputElement>) {
    e.persist();
    debounceFieldChange(e.target);
  };

  const onClose = function() {
    onRemove(onCancel);
  };

  function onChooserFieldChange({name, value, required = false}: TargetType) {
    setFormState({
      [name] : {
        value : isValue(value, 'none') ? null : value,
        required,
        name
      }
    });
  };

  //Devices Grid
  const toggleDetailGrid = (show = true) => function() {
    setViewDeviceDetailed(show);
  };

  function setDeviceDetailedRow(value) {
    setDeviceDetailedSelectedRow(value);
  };

  function getDeviceDetailedDoneBtnClick(value) {
    setDeviceDetailedDoneClicked(value);
  };

  const getDeviceSubGroupSwitchStatus = useCallback((value) => {
    setDeviceHasSubGroupSwitch(value);
  }, []);

  const getAssetSubGroupSwitchStatus = useCallback((value) => {
    setAssetHasSubGroupSwitch(value);
  }, []);

  //Assets Grid
  const toggleAssetDetailGrid = (show = true) => function() {
    setViewAssetDetailed(show);
  };

  function setAssetDetailedRow(value) {
    setAssetDetailedSelectedRow(value);
  };

  function getAssetDetailedDoneBtnClick(value) {
    setAssetDetailedDoneClicked(value);
  };

  function handleSubmit(mode: string) {
    return function() {
      const isEdit = mode === 'edit';
      onEdit(isEdit);
      onSubmit();
    };
  }

  const onAuditClick = useCallback((type: string) => {
    return function() {
      dispatch(actions[type](group));
    };
  }, []);

  const onChipClick = useCallback((value, isDeviceChip, chipName) => {
    const getId = value?.filter(item => item?.label === chipName);
    const id    = getId[0]?.id;
    if (isDeviceChip) {
      onViewDevice(id);
    } else {
      onViewAsset(id);
    }
  }, []);

  const handleSwitchChange = useCallback(({target}) => {
    const {name, checked, required = false} = target || {};
    setFormState({[name]: {name, value: checked, required}});
  }, []);

  return (
    <EditorDialog open={open}
      dialogTitle={
        translate(`common:editors.shared.${isEditMode ? viewGroup ? 'view' : 'edit' : 'add'}`, translations)
      }
      isViewDialog={viewGroup}
      onAuditClick={onAuditClick('groupAudit')}
      onEditClick={onEdit}
      groupsPermissions={groupsPermissions}
      globalGroup={globalGroup}
      account={group?.account}
      inheritable={group?.inheritable}
      ancestorsGroups={ancestorsGroups}
      hasInheritableGroups={hasInheritableGroups}
      onCloseDialog={onCloseDialog}
      onCancelDialog={onCloseDialog}
      omitActions={viewGroup}
      hasUpdatePermission
      onPrimaryButtonClick={onSubmit}
      primaryButtonLoading={loading}>
      <div className={classes.formWrapper}>
        <FormSection title={translate('groups:dialog.generalInfo')} bottomBorder>
          <>
            <FormItem fullWidth>
              <FormTextField name={GroupFormFields.Name}
                disabled={viewGroup}
                fieldLabel={translate('groups:dialog.fields.name.label')}
                placeholder={translate('groups:dialog.fields.name.placeholder')}
                hasError={formState[GroupFormFields.Name].error}
                errorText={translate('common:editors.shared.required')}
                defaultValue={formState[GroupFormFields.Name].value}
                onChange={onFieldChange}
                maxLength={64}
                required />
            </FormItem>
            <FormItem halfWidth>
              <FormTextField disabled
                name={GroupFormFields.Account}
                fieldLabel={translate('groups:dialog.fields.accName.label')}
                placeholder={translate('groups:dialog.fields.accName.placeholder')}
                defaultValue={formState[GroupFormFields.Account].value?.title || account?.title} />
            </FormItem>
            <FormItem halfWidth>
              <FormTextField name={GroupFormFields.Notes}
                disabled={viewGroup}
                fieldLabel={translate('groups:dialog.fields.notes.label')}
                placeholder={translate('groups:dialog.fields.notes.placeholder')}
                defaultValue={formState[GroupFormFields.Notes].value}
                onChange={onFieldChange}
                maxLength={1024} />
            </FormItem>
            <ConditionalRender condition={hasInheritableGroups}>
              <Inheritable name={GroupFormFields.Inheritable}
                onChange={handleSwitchChange}
                defaultValue={formState[GroupFormFields.Inheritable]?.value}
                disabled={viewGroup} />
            </ConditionalRender>
          </>
        </FormSection>
        <FormSection title={translate('groups:dialog.associations')}>
          <>
            <FormItem fullWidth>
              <AssetList accountId={entityId(account)}
                viewGroup={viewGroup}
                hasDetailedViewLink={!viewGroup}
                viewChip={viewChip}
                onChipClick={onChipClick}
                isChipClickable={isChipClickable}
                isEditMode={isEditMode}
                detailedViewLinkText={translate('groups:form.sections.assetDetailed')}
                onDetailedLinkClick={toggleAssetDetailGrid()}
                defaultValue={formState[GroupFormFields.Assets]?.value}
                isInfiniteScroll={isInfiniteScroll}
                setAssetDetailedRow={setAssetDetailedRow}
                loading={groupsLoading}
                onChange={function(value) {
                  onChooserFieldChange({
                    name : GroupFormFields.Assets,
                    value
                  });
                }} />
            </FormItem>
            <FormItem fullWidth>
              <DeviceList accountId={entityId(account)}
                viewGroup={viewGroup}
                hasDetailedViewLink={!viewGroup}
                viewChip={viewChip}
                onChipClick={onChipClick}
                isChipClickable={isChipClickable}
                isEditMode={isEditMode}
                detailedViewLinkText={translate('groups:form.sections.deviceDetailed')}
                onDetailedLinkClick={toggleDetailGrid()}
                defaultValue={formState[GroupFormFields.Devices]?.value}
                isInfiniteScroll={isInfiniteScroll}
                setDeviceDetailedRow={setDeviceDetailedRow}
                loading={groupsLoading}
                onChange={function(value) {
                  onChooserFieldChange({
                    name : GroupFormFields.Devices,
                    value
                  });
                }} />
            </FormItem>
            {viewDeviceDetailed && (
              <DeviceDetailView onBreadcrumbBackClick={toggleDetailGrid(false)}
                breadCrumbBackLabel={translate(`groups:form.sections.${isEditMode ? 'edit' : 'add'}Group`)}
                isEditMode={isEditMode}
                isCtcAdmin={isCtcAdmin}
                singleRowSelection={false}
                accountId={entityId(account)}
                Features={{}}
                hideSubGroups={isCtcAdmin}
                setDeviceDetailedRow={setDeviceDetailedRow}
                deviceDetailedSelectedRow={deviceDetailedSelectedRow}
                includeDeviceGridCheckboxes={includeDeviceGridCheckboxes}
                getDeviceDetailedDoneBtnClick={getDeviceDetailedDoneBtnClick}
                getDeviceSubGroupSwitchStatus={getDeviceSubGroupSwitchStatus}
                onCloseDialog={function() {
                  setViewDeviceDetailed(false);
                }}/>
            )}
            {viewAssetDetailed && (
              <AssetDetailView onBreadcrumbBackClick={toggleAssetDetailGrid(false)}
                isCtcAdmin={isCtcAdmin}
                isEditMode={isEditMode}
                accountId={entityId(account)}
                Features={{}}
                hideSubGroups={isCtcAdmin}
                setAssetDetailedRow={setAssetDetailedRow}
                assetDetailedSelectedRow={assetDetailedSelectedRow}
                includeAssetGridCheckboxes={includeAssetGridCheckboxes}
                getAssetDetailedDoneBtnClick={getAssetDetailedDoneBtnClick}
                getAssetSubGroupSwitchStatus={getAssetSubGroupSwitchStatus}
                searchPlaceholder={translate('groups:form.sections.searchPlaceholder')}
                onCloseDialog={function() {
                  setViewAssetDetailed(false);
                }}/>
            )}
          </>
        </FormSection>
      </div>
    </EditorDialog>
  );
};

export default AdminGroupEditor;
